/**
 * @file useToast.ts
 * @brief Imperative toast API + reactive store backing the global Toaster.
 *
 * @details
 * Pattern is the canonical shadcn/ui toast hook:
 * - Centralized in-memory store with subscribers (no React Context to
 *   avoid render-tree restrictions on emitters).
 * - `toast(...)` can be called from anywhere — query mutations, side
 *   effects, browser event handlers — without prop-drilling.
 * - `useToast()` returns the live state for the `<Toaster />` to render.
 *
 * @dependencies
 * - React (useState/useEffect for subscribers)
 * - components/ui/Toast (ToastActionElement type)
 *
 * @author DES
 * @date 26-05-2026
 */

import { useEffect, useState } from "react";
import type { ToastActionElement, ToastProps } from "@/components/ui/Toast";

// ----------------------------------------------------------------------
// Types & constants
// ----------------------------------------------------------------------
const TOAST_LIMIT = 4;
const TOAST_REMOVE_DELAY = 6_000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

interface State {
  toasts: ToasterToast[];
}

type Action =
  | { type: "ADD"; toast: ToasterToast }
  | { type: "UPDATE"; toast: Partial<ToasterToast> & { id: string } }
  | { type: "DISMISS"; toastId?: string }
  | { type: "REMOVE"; toastId?: string };

// ----------------------------------------------------------------------
// In-memory store
// ----------------------------------------------------------------------
let memoryState: State = { toasts: [] };
const listeners: Array<(state: State) => void> = [];
const removalTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  for (const listener of listeners) listener(memoryState);
}

function queueRemoval(toastId: string) {
  if (removalTimeouts.has(toastId)) return;
  const timeout = setTimeout(() => {
    removalTimeouts.delete(toastId);
    dispatch({ type: "REMOVE", toastId });
  }, TOAST_REMOVE_DELAY);
  removalTimeouts.set(toastId, timeout);
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "ADD":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case "DISMISS": {
      const { toastId } = action;
      if (toastId) queueRemoval(toastId);
      else state.toasts.forEach((t) => queueRemoval(t.id));
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t,
        ),
      };
    }

    case "REMOVE":
      return {
        ...state,
        toasts: action.toastId
          ? state.toasts.filter((t) => t.id !== action.toastId)
          : [],
      };
  }
}

// ----------------------------------------------------------------------
// Public imperative API
// ----------------------------------------------------------------------
let idCounter = 0;
function nextId(): string {
  idCounter = (idCounter + 1) % Number.MAX_SAFE_INTEGER;
  return idCounter.toString();
}

interface ToastInput extends Omit<ToasterToast, "id"> {}

export function toast(props: ToastInput) {
  const id = nextId();
  const dismiss = () => dispatch({ type: "DISMISS", toastId: id });
  const update = (next: Partial<ToasterToast>) =>
    dispatch({ type: "UPDATE", toast: { ...next, id } });

  dispatch({
    type: "ADD",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return { id, dismiss, update };
}

// ----------------------------------------------------------------------
// React hook subscribing to the store
// ----------------------------------------------------------------------
export function useToast() {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS", toastId }),
  };
}
